import React, { useState, useRef } from "react";
import styled from "styled-components";

export default function Contact() {
  const [formData, setFormData] = useState({
    fname: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const statusRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { fname, email, subject, message } = formData;
    if (!fname || !email || !subject || !message) return "All fields are required.";
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(email)) return "Invalid email format.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus({ success: false, message: validationError });
      statusRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/submit-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        setSubmitStatus({ success: true, message: 'Message sent successfully!' });
        setFormData({ fname: '', email: '', subject: '', message: '' });
      } else {
        throw new Error(data?.error || 'Failed to send message.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus({ success: false, message: error.message });
    } finally {
      setIsSubmitting(false);
      statusRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Wrapper id="contact">
      <div className="lightBg">
        <Container>
          <HeaderInfo>
            <h1 className="font40 extraBold">Let's get in touch</h1>
            <p className="font13">
              Innovative Digital Solutions Transforming your business with custom software and 
              data-driven insights.
            </p>
          </HeaderInfo>
          <ContentWrapper>
            <FormColumn>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="fname">First name:</Label>
                  <Input
                    id="fname"
                    name="fname"
                    value={formData.fname}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email:</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="subject">Subject:</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="message">Message:</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </SubmitButton>

                {submitStatus && (
                  <StatusMessage
                    ref={statusRef}
                    success={submitStatus.success}
                  >
                    {submitStatus.message}
                  </StatusMessage>
                )}
              </Form>
            </FormColumn>

            <MapColumn>
              <MapContainer>
                <iframe
                  title="Google Maps Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.818509999291!2d36.81721431475391!3d-1.281385899064661!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10d664f3d3c1%3A0x7e5c1c6b0a5a7b0!2sTech%20Dev%20Systems!5e0!3m2!1sen!2ske!4v1623345678909!5m2!1sen!2ske"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </MapContainer>
            </MapColumn>
          </ContentWrapper>
        </Container>
      </div>
    </Wrapper>
  );
}

// Styled Components Below (unchanged for brevity, same as your original)

const Wrapper = styled.section`width: 100%; padding: 40px 0;`;
const Container = styled.div`max-width: 1200px; margin: 0 auto; padding: 0 15px;`;
const HeaderInfo = styled.div`
  padding: 0 0 30px 0;
  text-align: center;
  h1 { margin-bottom: 15px; font-size: 2rem; }
  p { max-width: 600px; margin: 0 auto; }
  @media (max-width: 768px) { padding-bottom: 20px; h1 { font-size: 1.8rem; } }
`;
const ContentWrapper = styled.div`
  display: flex; gap: 30px;
  @media (max-width: 768px) { flex-direction: column; gap: 20px; }
`;
const FormColumn = styled.div`flex: 0 0 40%; padding-right: 20px; @media (max-width: 768px) { padding-right: 0; }`;
const MapColumn = styled.div`flex: 0 0 60%; height: 400px; @media (max-width: 768px) { height: 300px; }`;
const Form = styled.form`
  padding: 25px; background: white; border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex; flex-direction: column;
`;
const FormGroup = styled.div`margin-bottom: 20px;`;
const Label = styled.label`display: block; margin-bottom: 8px; font-size: 14px; color: #555; font-weight: 500;`;
const Input = styled.input`
  width: 100%; padding: 10px 12px; border: 1px solid #ddd;
  border-radius: 6px; font-size: 15px;
  &:focus { border-color: #7620ff; box-shadow: 0 0 0 2px rgba(118, 32, 255, 0.2); outline: none; }
  &:disabled { background-color: #f5f5f5; cursor: not-allowed; }
`;
const TextArea = styled.textarea`
  width: 100%; min-height: 100px; padding: 10px 12px; border: 1px solid #ddd;
  border-radius: 6px; font-size: 15px; resize: vertical;
  &:focus { border-color: #7620ff; box-shadow: 0 0 0 2px rgba(118, 32, 255, 0.2); outline: none; }
  &:disabled { background-color: #f5f5f5; cursor: not-allowed; }
`;
const SubmitButton = styled.button`
  background-color: #7620ff; color: white; border: none; padding: 12px 20px;
  font-size: 15px; font-weight: 600; border-radius: 6px; cursor: pointer;
  width: 100%; margin-top: 10px; display: flex; justify-content: center; gap: 8px;
  &:hover { background-color: #580cd2; }
  &:disabled { background-color: #a566ff; cursor: not-allowed; opacity: 0.7; }
`;
const Spinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3); border-radius: 50%;
  border-top: 2px solid white; width: 16px; height: 16px;
  animation: spin 1s linear infinite;
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;
const StatusMessage = styled.div`
  margin-top: 15px; padding: 10px 12px; border-radius: 6px; font-size: 14px;
  background-color: ${props => props.success ? '#d4edda' : '#f8d7da'};
  color: ${props => props.success ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.success ? '#c3e6cb' : '#f5c6cb'};
`;
const MapContainer = styled.div`
  width: 100%; height: 100%; border-radius: 8px; overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;
